<script setup lang="ts">
import type { GiratudoPlacarEntry } from '@/types/giratudo';
import { getAssetsUrlForCDN } from '@/utils/template';

interface Props {
  data: GiratudoPlacarEntry;
  gridCols: string;
  empty?: boolean;
}

const { data, gridCols, empty = false } = defineProps<Props>();

function getPosBadgeUrl(position: number): string {
  if (position <= 3)
    return getAssetsUrlForCDN(`/assets/images/giratudo/${position}.png`);

  return getAssetsUrlForCDN('/assets/images/giratudo/4.png');
}

const { userName } = useUserData();

const displayName = computed(() => {
  if (userName.value) return userName.value.split(' ')[0];

  return data.username || '';
});
</script>

<template>
  <div
    class="mx-2 mt-2 mb-1.5 flex flex-col overflow-hidden rounded-[13px] user-position-wrapper"
  >
    <!-- Label -->
    <div class="px-2 py-1 text-center">
      <span
        class="text-[10px] font-semibold uppercase tracking-widest text-white"
      >
        SUA POSIÇÃO
      </span>
    </div>

    <!-- Row — mesmo grid/padding das rows da tabela -->
    <div
      class="grid items-center text-[11px] font-semibold text-white"
      :style="{
        gridTemplateColumns: gridCols,
        height: '36px',
        minHeight: '36px',
        maxHeight: '36px',
      }"
    >
      <!-- POS -->
      <span class="flex items-center justify-center">
        <span
          v-if="empty"
          class="inline-flex items-center justify-center text-[10px] font-semibold text-white"
          style="
            padding: 8px 8px;
            border-radius: 8px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            background: rgba(255, 255, 255, 0.08);
          "
        >
          --
        </span>
        <span
          v-else-if="data.position <= 10"
          class="inline-flex h-7 w-7 items-center justify-center bg-contain bg-center bg-no-repeat pos-badge"
          :class="[data.position <= 3 ? 'pos-badge-top' : 'pb-1']"
          :style="{
            backgroundImage: `url(${getPosBadgeUrl(data.position)})`,
          }"
        >
          {{ data.position }}
        </span>
        <span
          v-else
          class="pos-badge pos-badge-plain"
        >
          {{ data.position }}
        </span>
      </span>

      <!-- NOME -->
      <span class="truncate pl-1 font-semibold text-white">
        {{ empty ? '---' : displayName }}
      </span>

      <!-- PTS -->
      <span class="text-center text-white">
        {{ empty ? '---' : data.ticketNumber }}
      </span>

      <!-- PREMIO -->
      <span class="text-center text-white">
        {{ empty ? '---' : data.prize }}
      </span>

      <!-- CRIADO -->
      <span class="text-center text-white">
        {{ empty ? '---' : data.createdAt }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.user-position-wrapper {
  border: 1.157px solid var(--colors-base-primary, #1414dd);
  background: linear-gradient(
    90deg,
    rgba(20, 20, 221, 0.7) 0%,
    rgba(20, 20, 221, 0.2) 100%
  );
  box-shadow:
    0 11.571px 17.356px -3.471px rgba(0, 112, 243, 0.2),
    0 4.628px 6.942px -2.314px rgba(0, 0, 0, 0.05);
}

.pos-badge {
  color: #f5feff;
  text-align: center;
  text-shadow: 0.295px 0.295px 0.393px #00054c;
  -webkit-text-stroke-width: 0.2px;
  -webkit-text-stroke-color: #1d23df;
  font-size: 10.81px;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
}

.pos-badge-top {
  -webkit-text-stroke-width: 0.6px;
  font-size: 13px;
  padding-top: 6px;
  box-sizing: border-box;
}

.pos-badge-plain {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10.81px;
  -webkit-text-stroke-width: 0;
}
</style>
